import { useState } from 'react';
import { buyerAddress, buyerIdLabel } from '../lib/taxes.ts';
import { createPortal } from 'react-dom';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, money, parseMoney } from '../lib/format.ts';
import { DEFAULT_LOOK, LookPicker, PaperEnd, PaperLogoRight, paperAttrs, PaperSeller, useDocLook, type PaperLook } from '../lib/doclook.tsx';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { byRate, docName, InvoiceEditor, parseNumber, priceText, qtyText, Totals, type Line, type Sellable, type Settings } from './Invoices.tsx';
import { DeliveryBadge, DeliveryEditor, deliveryName } from './Deliveries.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { RecordNotes } from '../components/RecordNotes.tsx';

type QuoteState = 'draft' | 'sent' | 'confirmed' | 'done' | 'declined' | 'cancelled';

export interface QuoteRow {
  id: string;
  number: number;
  year: number;
  state: QuoteState;
  customer: string;
  customer_name: string;
  date: string;
  valid_until: string;
  delivery_date: string | null;
  reference: string | null;
  salesperson?: string | null;
  total: number;
  confirmed_at: string | null;
  expired: boolean;
  invoiced: number;
}
export interface QuoteDetail {
  quote: {
    id: string;
    salesperson?: string | null;
    number: number;
    year: number;
    name: string;
    state: QuoteState;
    order: boolean;
    expired: boolean;
    customer: string;
    date: string;
    valid_until: string;
    delivery_date: string | null;
    location: string;
    location_name: string;
    reference: string | null;
    note: string | null;
    net: number;
    tax: number;
    total: number;
    sent_at: string | null;
    confirmed_at: string | null;
    closed_reason: string | null;
    created_by_name: string | null;
    confirmed_by_name: string | null;
    share_token: string | null;
    viewed_at: string | null;
    accepted_name: string | null;
    accepted_at: string | null;
  };
  lines: (Omit<Line, 'credited'> & { invoiced: number; drafted: number; delivered: number; order_line?: null })[];
  /** Optional extras the customer may add; not counted in the total. */
  options: Omit<Line, 'credited'>[];
  deliveries: { id: string; number: number | null; year: number | null; state: 'draft' | 'done'; date: string }[];
  invoices: { id: string; kind: 'invoice' | 'credit'; number: number | null; year: number | null; state: 'draft' | 'issued' | 'paid' | 'cancelled'; date: string; total: number }[];
  customer: { id: string; name: string; tax_id: string | null; id_type: string | null; address: string | null; town: string | null; country: string; active: boolean };
  company: { legal_name: string; trade_name: string | null; tax_id: string | null; address: string | null; city: string | null; country: string; phone: string | null; email: string | null };
  settings: Settings;
  branch: { name: string; address: string | null; city: string | null };
}

/** "Q 4/2026" for a quote, "SO 4/2026" once the customer accepted it. */
export const quoteName = (q: { number: number; year: number; confirmed_at?: string | null; order?: boolean }) =>
  `${q.order || q.confirmed_at ? t('quote.so_prefix') : t('quote.q_prefix')} ${q.number}/${q.year}`;

export function QuoteBadge({ q }: { q: Pick<QuoteRow, 'state' | 'expired' | 'invoiced'> }) {
  switch (q.state) {
    case 'draft':
      return q.expired ? <StatusBadge tone="warn" icon="clock" label={t('quote.st_expired')} /> : <StatusBadge tone="neutral" icon="edit" label={t('quote.st_draft')} />;
    case 'sent':
      return q.expired ? <StatusBadge tone="warn" icon="clock" label={t('quote.st_expired')} /> : <StatusBadge tone="info" icon="send" label={t('quote.st_sent')} />;
    case 'confirmed':
      return <StatusBadge tone="warn" icon="invoice" label={q.invoiced ? t('quote.st_part') : t('quote.st_to_invoice')} />;
    case 'done':
      return <StatusBadge tone="ok" icon="check" label={t('quote.st_done')} />;
    case 'declined':
      return <StatusBadge tone="neutral" icon="x" label={t('quote.st_declined')} />;
    default:
      return <StatusBadge tone="neutral" icon="x" label={t('quote.st_cancelled')} />;
  }
}

const FILTERS = ['open', 'quotes', 'orders', 'done', 'closed', 'all'] as const;

/** Quotes waiting for an answer and orders waiting to be invoiced. */
export function QuoteList({ onOpen }: { onOpen: (id: string) => void }) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('open');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const { data } = useAction<QuoteRow[]>('invoicing.quotes', { filter, search: q || undefined });
  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="radiogroup" aria-label={t('inv.show')}>
          {FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`quote.f_${f}`)}
            </button>
          ))}
        </div>
        <form
          className="search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(search.trim());
          }}
        >
          <Icon name="search" size={18} />
          <input className="input" placeholder={t('inv.search')} aria-label={t('inv.search')} value={search} onChange={(e) => setSearch(e.target.value)} onBlur={() => setQ(search.trim())} />
          {search ? <IconButton icon="x" label={t('common.clear')} onClick={() => (setSearch(''), setQ(''))} /> : null}
        </form>
      </div>
      <section className="list-card" aria-label={t('inv.tab_quotes')}>
        {!data ? (
          <Skeleton h={160} />
        ) : data.length === 0 ? (
          <EmptyState icon="note" title={q ? t('order.no_results') : t('quote.none')} body={q ? undefined : t('quote.none_body')} />
        ) : (
          data.map((d) => (
            <button key={d.id} className="list-row stock-row" onClick={() => onOpen(d.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name={d.confirmed_at ? 'bag' : 'note'} size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">
                  {quoteName(d)} · {d.customer_name}
                </span>
                <span className="list-sub">
                  {[
                    day(d.date),
                    d.state === 'draft' || d.state === 'sent' ? t('quote.valid_to', { day: day(d.valid_until) }) : null,
                    d.state === 'confirmed' && d.delivery_date ? t('quote.deliver_on', { day: day(d.delivery_date) }) : null,
                    d.reference,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className="stock-qty">
                <strong>{money(d.total)}</strong>
                {d.state === 'confirmed' && d.invoiced ? <span className="muted">{t('quote.invoiced_of', { amount: money(d.invoiced) })}</span> : null}
              </span>
              <QuoteBadge q={d} />
            </button>
          ))
        )}
      </section>
    </>
  );
}

/** One quote or order: as it prints, and what can happen to it next. */
export function QuoteDialog({
  id,
  onClose,
  onOpenInvoice,
  onOpenDelivery,
  onChange,
}: {
  id: string;
  onClose: () => void;
  onOpenInvoice: (id: string) => void;
  onOpenDelivery: (id: string) => void;
  onChange: () => Promise<void>;
}) {
  const { data: d, reload } = useAction<QuoteDetail>('invoicing.quote', { quote: id });
  const [editing, setEditing] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const refresh = async () => {
    await Promise.all([reload(), onChange()]);
  };
  if (!d) {
    return (
      <Dialog title={t('quote.quote')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  if (editing) {
    return <InvoiceEditor detail={null} quote={d} onClose={() => setEditing(false)} onSaved={async () => (setEditing(false), await refresh())} />;
  }
  if (delivering) {
    return (
      <DeliveryEditor
        order={d.quote.id}
        onClose={() => setDelivering(false)}
        onSaved={async (made) => {
          setDelivering(false);
          await refresh();
          onOpenDelivery(made);
        }}
      />
    );
  }
  return <QuoteView d={d} onClose={onClose} onEdit={() => setEditing(true)} onDeliver={() => setDelivering(true)} onOpenInvoice={onOpenInvoice} onOpenDelivery={onOpenDelivery} onChange={refresh} />;
}

function QuoteView({
  d,
  onClose,
  onEdit,
  onDeliver,
  onOpenInvoice,
  onOpenDelivery,
  onChange,
}: {
  d: QuoteDetail;
  onClose: () => void;
  onEdit: () => void;
  onDeliver: () => void;
  onOpenInvoice: (id: string) => void;
  onOpenDelivery: (id: string) => void;
  onChange: () => Promise<void>;
}) {
  const { can } = useSession();
  const { toast } = useUi();
  const { busy, run } = useSave();
  const q = d.quote;
  const manage = can('invoicing.manage');
  const [mode, setMode] = useState<'decline' | 'cancel' | 'part' | null>(null);
  const [printing, setPrinting] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const look = useDocLook('quote');
  const isQuote = q.state === 'draft' || q.state === 'sent';
  const toInvoice = d.lines.reduce((s, l) => s + Math.max(0, l.qty - l.invoiced), 0) > 0;
  const toDeliver = d.lines.some((l) => l.qty - l.delivered > 1e-9);
  const draftNote = d.deliveries.find((n) => n.state === 'draft');
  const issued = d.invoices.filter((i) => i.state !== 'draft');
  const draft = d.invoices.find((i) => i.state === 'draft');
  const print = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 50);
  };
  const share = () =>
    void run(async () => {
      const { path } = await action<{ path: string }>('invoicing.share_quote', { quote: q.id });
      const link = `${window.location.origin}${path}`;
      await navigator.clipboard?.writeText(link).catch(() => undefined);
      setLink(link);
      await onChange();
    }, t('qopt.link_copied'));
  const step = (name: string, done: string, input: Record<string, unknown> = {}) =>
    void run(async () => {
      await action(name, { quote: q.id, ...input });
      await onChange();
    }, done);
  const invoice = (lines?: { line: string; qty: number }[]) =>
    void run(async () => {
      const made = await action<{ invoice: string; existing: boolean }>('invoicing.invoice_order', { order: q.id, lines });
      if (made.existing) toast(t('quote.draft_open'));
      await onChange();
      onOpenInvoice(made.invoice);
    });

  return (
    <Dialog
      title={`${quoteName(q)} · ${d.customer.name}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            <Button icon="printer" onClick={print}>
              {t('inv.print')}
            </Button>
            <LookPicker {...look} />
            {manage && isQuote ? (
              <Button icon="edit" onClick={onEdit}>
                {t('quote.edit')}
              </Button>
            ) : null}
            {manage && isQuote ? (
              <Button icon="link" onClick={share} busy={busy}>
                {t('qopt.share')}
              </Button>
            ) : null}
            {manage && q.state === 'draft' ? (
              <Button icon="send" onClick={() => step('invoicing.quote_sent', t('quote.marked_sent'))} busy={busy}>
                {t('quote.mark_sent')}
              </Button>
            ) : null}
            {manage && isQuote ? (
              <Button variant="ghost" icon="x" onClick={() => setMode('decline')}>
                {t('quote.decline')}
              </Button>
            ) : null}
            {manage && q.state === 'confirmed' && !issued.length ? (
              <Button variant="ghost" icon="x" onClick={() => setMode('cancel')}>
                {t('quote.cancel_order')}
              </Button>
            ) : null}
            {manage && (q.state === 'confirmed' || q.state === 'done') && (toDeliver || draftNote) ? (
              <Button icon="truck" onClick={() => (draftNote ? onOpenDelivery(draftNote.id) : onDeliver())}>
                {draftNote ? t('dn.open_draft') : t('dn.new')}
              </Button>
            ) : null}
            {manage && q.state === 'confirmed' && toInvoice && !draft && d.lines.length > 1 ? (
              <Button icon="split" onClick={() => setMode('part')}>
                {t('quote.invoice_part')}
              </Button>
            ) : null}
          </span>
          {manage && isQuote ? (
            <Button variant="primary" icon="check" onClick={() => step('invoicing.confirm_quote', t('quote.confirmed'))} busy={busy}>
              {t('quote.accept')}
            </Button>
          ) : null}
          {manage && q.state === 'declined' ? (
            <Button variant="primary" icon="undo" onClick={() => step('invoicing.reopen_quote', t('quote.reopened'))} busy={busy}>
              {t('quote.reopen')}
            </Button>
          ) : null}
          {manage && q.state === 'confirmed' && toInvoice ? (
            <Button variant="primary" icon="invoice" onClick={() => (draft ? onOpenInvoice(draft.id) : invoice())} busy={busy}>
              {draft ? t('quote.open_draft') : issued.length ? t('quote.invoice_rest') : t('quote.make_invoice')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-stack">
        <div className="item-summary">
          <div>
            <span className="muted">{t('inv.total')}</span>
            <strong>{money(q.total)}</strong>
          </div>
          {isQuote ? (
            <div>
              <span className="muted">{t('quote.valid_until')}</span>
              <strong className={q.expired ? 'text-danger' : ''}>{day(q.valid_until)}</strong>
            </div>
          ) : null}
          {q.delivery_date ? (
            <div>
              <span className="muted">{t('quote.delivery_date')}</span>
              <strong>{day(q.delivery_date)}</strong>
            </div>
          ) : null}
          {q.order ? (
            <div>
              <span className="muted">{t('quote.invoiced')}</span>
              <strong>{money(issued.filter((i) => i.kind === 'invoice').reduce((s, i) => s + i.total, 0))}</strong>
            </div>
          ) : null}
          <QuoteBadge q={{ state: q.state, expired: q.expired, invoiced: issued.length }} />
        </div>
        {link ? (
          <div className="callout callout-info quote-link" role="status">
            <Icon name="link" size={16} />
            <div>
              <p className="strong">{t('qopt.link_ready')}</p>
              <input className="input" readOnly value={link} aria-label={t('qopt.link')} onFocus={(e) => e.currentTarget.select()} />
              <p className="muted small">{t('qopt.link_hint')}</p>
            </div>
            <a className="btn btn-ghost btn-sm" href={link} target="_blank" rel="noopener">
              <Icon name="eye" size={16} /> {t('qopt.open_link')}
            </a>
          </div>
        ) : null}
        {q.accepted_at ? (
          <p className="callout callout-ok">
            <Icon name="checkCircle" size={16} />
            <span>{t('qopt.accepted_by', { name: q.accepted_name ?? '', when: dateTime(q.accepted_at) })}</span>
          </p>
        ) : isQuote && q.share_token ? (
          <p className="callout callout-info">
            <Icon name={q.viewed_at ? 'eye' : 'send'} size={16} />
            <span>{q.viewed_at ? t('qopt.viewed', { when: dateTime(q.viewed_at) }) : t('qopt.not_viewed')}</span>
          </p>
        ) : null}
        {q.expired ? (
          <p className="callout callout-warn">
            <Icon name="clock" size={16} />
            <span>{t('quote.expired_note', { day: day(q.valid_until) })}</span>
          </p>
        ) : null}
        {(q.state === 'declined' || q.state === 'cancelled') && q.closed_reason ? (
          <p className="callout callout-info">
            <Icon name="note" size={16} />
            <span>{t(q.state === 'declined' ? 'quote.declined_because' : 'quote.cancelled_because', { reason: q.closed_reason })}</span>
          </p>
        ) : null}
        {mode === 'decline' ? <ReasonBox title={t('quote.decline_title')} optional button={t('quote.decline')} onCancel={() => setMode(null)} onDone={(reason) => step('invoicing.decline_quote', t('quote.declined'), { reason })} /> : null}
        {mode === 'cancel' ? <ReasonBox title={t('quote.cancel_title')} button={t('quote.cancel_order')} onCancel={() => setMode(null)} onDone={(reason) => step('invoicing.cancel_quote', t('quote.cancelled'), { reason })} /> : null}
        {mode === 'part' ? <PartBox d={d} busy={busy} onCancel={() => setMode(null)} onDone={(lines) => invoice(lines)} /> : null}

        {q.order ? <OrderProgress d={d} /> : null}

        {isQuote && d.options.length ? (
          <section className="item-moves">
            <h3>{t('qopt.title')}</h3>
            <p className="muted">{t('qopt.view_hint')}</p>
            <div className="list-card">
              {d.options.map((o) => (
                <div key={o.id} className="list-row compact-move">
                  <span className="list-icon" aria-hidden="true">
                    <Icon name="plus" size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{o.name}</span>
                    <span className="list-sub">
                      {qtyText(o.qty)} {t(`inv.u_${o.unit}` as MessageKey)} · {money(o.total)}
                    </span>
                  </span>
                  {manage ? (
                    <Button size="sm" icon="plus" onClick={() => step('invoicing.add_quote_option', t('qopt.added'), { option: o.id })} busy={busy}>
                      {t('qopt.add_to_quote')}
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <div className="inv-preview">
          <QuotePaper d={d} look={look.paper} />
        </div>

        {d.deliveries.length ? (
          <section className="item-moves">
            <h3>{t('dn.for_order')}</h3>
            <div className="list-card">
              {d.deliveries.map((r) => (
                <button key={r.id} className="list-row compact-move" onClick={() => onOpenDelivery(r.id)}>
                  <span className="list-icon" aria-hidden="true">
                    <Icon name="truck" size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{deliveryName(r)}</span>
                    <span className="list-sub">{day(r.date)}</span>
                  </span>
                  <DeliveryBadge state={r.state} />
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {d.invoices.length ? (
          <section className="item-moves">
            <h3>{t('quote.invoices')}</h3>
            <div className="list-card">
              {d.invoices.map((r) => (
                <button key={r.id} className="list-row compact-move" onClick={() => onOpenInvoice(r.id)}>
                  <span className="list-icon" aria-hidden="true">
                    <Icon name="invoice" size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{docName(r)}</span>
                    <span className="list-sub">{day(r.date)}</span>
                  </span>
                  <strong>{money(r.total)}</strong>
                </button>
              ))}
            </div>
          </section>
        ) : null}
        <RecordNotes model="invoicing.quote" record={q.id} version={d} />
      </div>
      {printing
        ? createPortal(
            <div className="print-sheet print-invoice">
              <QuotePaper d={d} look={look.paper} />
            </div>,
            document.body,
          )
        : null}
    </Dialog>
  );
}

/** How much of each order line is invoiced. */
function OrderProgress({ d }: { d: QuoteDetail }) {
  return (
    <section className="item-moves">
      <h3>{t('quote.progress')}</h3>
      <div className="list-card">
        {d.lines.map((l) => {
          const pct = l.qty ? Math.min(100, Math.round((l.invoiced / l.qty) * 100)) : 100;
          return (
            <div key={l.id} className="list-row compact-move order-line">
              <span className="list-main">
                <span className="list-title">{l.name}</span>
                <span className="list-sub">
                  {t('quote.line_invoiced', { done: qtyText(l.invoiced), of: qtyText(l.qty), unit: t(`inv.u_${l.unit}` as MessageKey) })}
                  {l.drafted ? ` · ${t('quote.line_drafted', { qty: qtyText(l.drafted) })}` : ''}
                  {` · ${t('dn.line_delivered', { done: qtyText(l.delivered), of: qtyText(l.qty) })}`}
                </span>
              </span>
              <span className="order-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={l.name}>
                <span style={{ width: `${pct}%` }} />
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function ReasonBox({ title, button, optional, onCancel, onDone }: { title: string; button: string; optional?: boolean; onCancel: () => void; onDone: (reason: string | null) => void }) {
  const [reason, setReason] = useState('');
  const ok = optional || reason.trim().length >= 3;
  return (
    <section className="pay-box" aria-label={title}>
      <h3>{title}</h3>
      <Field label={t('quote.reason')} hint={optional ? t('quote.reason_optional') : undefined}>
        <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} data-autofocus />
      </Field>
      <div className="row-actions">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="danger" icon="x" disabled={!ok} onClick={() => onDone(reason.trim() || null)}>
          {button}
        </Button>
      </div>
    </section>
  );
}

/** Invoices some quantities now, e.g. what was delivered so far. */
function PartBox({ d, busy, onCancel, onDone }: { d: QuoteDetail; busy: boolean; onCancel: () => void; onDone: (lines: { line: string; qty: number }[]) => void }) {
  const open = d.lines.filter((l) => l.qty - l.invoiced > 0);
  const [qty, setQty] = useState<Record<string, string>>(Object.fromEntries(open.map((l) => [l.id, qtyText(l.qty - l.invoiced)])));
  const picked = open.map((l) => ({ l, v: qty[l.id]?.trim() ? parseNumber(qty[l.id]!) : 0 }));
  const bad = picked.some(({ l, v }) => v === null || v < 0 || v > l.qty - l.invoiced + 1e-9);
  const lines = picked.filter(({ v }) => (v ?? 0) > 0).map(({ l, v }) => ({ line: l.id, qty: v! }));
  return (
    <section className="pay-box" aria-label={t('quote.invoice_part')}>
      <h3>{t('quote.invoice_part')}</h3>
      <p className="muted">{t('quote.part_hint')}</p>
      <div className="list-card">
        {picked.map(({ l, v }) => (
          <div key={l.id} className="list-row compact-move">
            <span className="list-main">
              <span className="list-title">{l.name}</span>
              <span className="list-sub">{t('quote.left_to_invoice', { qty: qtyText(l.qty - l.invoiced), unit: t(`inv.u_${l.unit}` as MessageKey) })}</span>
            </span>
            <input
              className={`input inv-credit-qty${v === null || (v ?? 0) > l.qty - l.invoiced + 1e-9 ? ' invalid' : ''}`}
              inputMode="decimal"
              aria-label={l.name}
              value={qty[l.id] ?? ''}
              onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })}
            />
          </div>
        ))}
      </div>
      <div className="row-actions">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" icon="invoice" disabled={bad || !lines.length} busy={busy} onClick={() => onDone(lines)}>
          {t('quote.make_invoice')}
        </Button>
      </div>
    </section>
  );
}

/** The quote or the order confirmation on A4. */
export function QuotePaper({ d, look }: { d: QuoteDetail; look?: PaperLook | null }) {
  const q = d.quote;
  const c = d.company;
  const b = d.customer;
  const L = look?.look ?? DEFAULT_LOOK;
  const discounts = d.lines.some((l) => l.discount);
  const by = L.show.issued_by ? q.created_by_name : null;
  return (
    <article {...paperAttrs(look)} aria-label={quoteName(q)}>
      <header className="inv-paper-head">
        <PaperSeller c={c} p={look} />
        <div className="inv-paper-title">
          <PaperLogoRight p={look} />
          <h2>{q.order ? t('quote.p_order') : t('quote.p_quote')}</h2>
          <p className="inv-number">{q.name}</p>
          <dl>
            <dt>{t('quote.date')}</dt>
            <dd>{day(q.date)}</dd>
            {!q.order ? (
              <>
                <dt>{t('quote.valid_until')}</dt>
                <dd>{day(q.valid_until)}</dd>
              </>
            ) : null}
            {q.delivery_date ? (
              <>
                <dt>{t('quote.delivery_date')}</dt>
                <dd>{day(q.delivery_date)}</dd>
              </>
            ) : null}
            {q.reference ? (
              <>
                <dt>{t('inv.reference')}</dt>
                <dd>{q.reference}</dd>
              </>
            ) : null}
          </dl>
        </div>
      </header>
      <section className="inv-paper-parties">
        <div>
          <p className="inv-label">{t('quote.p_for')}</p>
          <p className="strong">{b.name}</p>
          {b.tax_id ? <p>{t('inv.p_id', { type: buyerIdLabel(b), id: b.tax_id })}</p> : null}
          <p>{buyerAddress(b)}</p>
        </div>
        {d.branch.name ? (
          <div>
            <p className="inv-label">{t('quote.p_from')}</p>
            <p>{d.branch.name}</p>
            <p>{[d.branch.address, d.branch.city].filter(Boolean).join(', ')}</p>
          </div>
        ) : null}
      </section>
      <table className="inv-paper-lines">
        <thead>
          <tr>
            {L.lines.number ? <th>#</th> : null}
            <th>{t('inv.description')}</th>
            <th className="num">{t('inv.qty')}</th>
            <th className="num">{t('inv.unit_price')}</th>
            {discounts ? <th className="num">{t('inv.discount')}</th> : null}
            <th className="num">{t('inv.vat')}</th>
            <th className="num">{t('inv.net')}</th>
          </tr>
        </thead>
        <tbody>
          {d.lines.map((l, i) => (
            <tr key={l.id}>
              {L.lines.number ? <td>{i + 1}</td> : null}
              <td>{l.name}</td>
              <td className="num">
                {qtyText(l.qty)}
                {L.lines.unit ? ` ${t(`inv.u_${l.unit}` as MessageKey)}` : ''}
              </td>
              <td className="num">{money(l.unit_price)}</td>
              {discounts ? <td className="num">{l.discount ? `${l.discount} %` : ''}</td> : null}
              <td className="num">{l.tax_rate} %</td>
              <td className="num">{money(l.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!q.order && d.options.length ? (
        <section className="inv-paper-options">
          <p className="inv-label">{t('qopt.p_title')}</p>
          <table className="inv-paper-lines">
            <tbody>
              {d.options.map((o) => (
                <tr key={o.id}>
                  <td>{o.name}</td>
                  <td className="num">
                    {qtyText(o.qty)}
                    {L.lines.unit ? ` ${t(`inv.u_${o.unit}` as MessageKey)}` : ''}
                  </td>
                  <td className="num">{money(o.unit_price)}</td>
                  <td className="num">{o.tax_rate} %</td>
                  <td className="num">{money(o.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
      <div className="inv-paper-foot">
        <div className="inv-paper-pay">
          {!q.order ? <p>{t('quote.p_valid', { day: day(q.valid_until) })}</p> : <p>{t('quote.p_thanks')}</p>}
          {q.note ? <p>{q.note}</p> : null}
          {L.show.terms && d.settings.terms ? <p className="muted">{d.settings.terms}</p> : null}
        </div>
        <Totals net={q.net} tax={q.tax} rates={byRate(d.lines)} />
      </div>
      {by && !L.show.signatures ? <p className="inv-paper-by muted">{t('quote.p_by', { name: by })}</p> : null}
      <PaperEnd p={look} by={by} buyerLabel={q.order ? undefined : t('look.p_accepted_sign')} />
    </article>
  );
}

/* ---------------------------------------------------------------- Price lists */

interface PricelistRow {
  id: string;
  name: string;
  discount: number;
  active: boolean;
  note: string | null;
  prices: number;
  customers: number;
}
interface PricelistDetail {
  pricelist: { id: string; name: string; discount: number; active: boolean; note: string | null };
  prices: { id: string; product: string; name: string; sku: string | null; price: number; base: number }[];
  customers: { id: string; name: string }[];
}

/** Price lists: a percentage off for some customers, and their own prices for some products. */
export function PricelistsTab({ creating, onCreated }: { creating: boolean; onCreated: () => void }) {
  const { can } = useSession();
  const [archived, setArchived] = useState(false);
  const { data, reload } = useAction<PricelistRow[]>('invoicing.pricelists', { archived });
  const [open, setOpen] = useState<string | null>(null);
  const done = async () => {
    setOpen(null);
    onCreated();
    await reload();
  };
  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="radiogroup" aria-label={t('inv.show')}>
          {([false, true] as const).map((a) => (
            <button key={String(a)} role="radio" aria-checked={archived === a} className={archived === a ? 'active' : ''} onClick={() => setArchived(a)}>
              {a ? t('pl.archived') : t('pl.active')}
            </button>
          ))}
        </div>
      </div>
      <p className="muted">{t('pl.intro')}</p>
      <section className="list-card" aria-label={t('inv.tab_pricelists')}>
        {!data ? (
          <Skeleton h={120} />
        ) : data.length === 0 ? (
          <EmptyState icon="percent" title={t('pl.none')} body={archived ? undefined : t('pl.none_body')} />
        ) : (
          data.map((p) => (
            <button key={p.id} className="list-row" onClick={() => setOpen(p.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name="percent" size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">{p.name}</span>
                <span className="list-sub">
                  {[p.discount ? t('pl.off_all', { pct: p.discount }) : t('pl.no_discount'), p.prices ? tn('pl.own_prices', p.prices) : null, tn('pl.customers_n', p.customers)].filter(Boolean).join(' · ')}
                </span>
              </span>
              <Icon name="chevronRight" size={18} />
            </button>
          ))
        )}
      </section>
      {open || creating ? <PricelistEditor id={open} canEdit={can('invoicing.pricelists')} onClose={() => (setOpen(null), onCreated())} onSaved={done} /> : null}
    </>
  );
}

function PricelistEditor({ id, canEdit, onClose, onSaved }: { id: string | null; canEdit: boolean; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: products } = useAction<Sellable[]>('invoicing.products');
  if (!products) {
    return (
      <Dialog title={t('pl.pricelist')} onClose={onClose} size="lg">
        <Skeleton h={240} />
      </Dialog>
    );
  }
  return id ? <PricelistLoad id={id} products={products} canEdit={canEdit} onClose={onClose} onSaved={onSaved} /> : <PricelistForm d={null} products={products} canEdit={canEdit} onClose={onClose} onSaved={onSaved} />;
}

function PricelistLoad({ id, ...rest }: { id: string; products: Sellable[]; canEdit: boolean; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data } = useAction<PricelistDetail>('invoicing.pricelist', { pricelist: id });
  if (!data) {
    return (
      <Dialog title={t('pl.pricelist')} onClose={rest.onClose} size="lg">
        <Skeleton h={240} />
      </Dialog>
    );
  }
  return <PricelistForm d={data} {...rest} />;
}

function PricelistForm({ d, products, canEdit, onClose, onSaved }: { d: PricelistDetail | null; products: Sellable[]; canEdit: boolean; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(d?.pricelist.name ?? '');
  const [discount, setDiscount] = useState(d?.pricelist.discount ? String(d.pricelist.discount) : '');
  const [note, setNote] = useState(d?.pricelist.note ?? '');
  const [rows, setRows] = useState<{ product: string; price: string }[]>(d?.prices.map((p) => ({ product: p.product, price: priceText(p.price) })) ?? []);
  const byId = new Map(products.map((p) => [p.id, p]));
  const pct = discount.trim() ? parseNumber(discount) : 0;
  const pctBad = pct === null || pct < 0 || pct > 100;
  const parsed = rows.map((r) => ({ ...r, value: parseMoney(r.price) }));
  const twice = (i: number) => !!parsed[i]!.product && parsed.findIndex((r) => r.product === parsed[i]!.product) !== i;
  const bad = pctBad || name.trim().length < 2 || parsed.some((r, i) => !r.product || r.value === null || twice(i));
  const used = new Set(rows.map((r) => r.product));
  const save = () =>
    void run(async () => {
      await action('invoicing.save_pricelist', {
        pricelist: d?.pricelist.id,
        name: name.trim(),
        discount: pct ?? 0,
        note: note.trim() || null,
        prices: parsed.map((r) => ({ product: r.product, price: r.value })),
      });
      await onSaved();
    }, t('pl.saved'));
  const archive = (active: boolean) =>
    void run(async () => {
      await action('invoicing.archive_pricelist', { pricelist: d!.pricelist.id, active });
      await onSaved();
    }, active ? t('pl.restored') : t('pl.archived_done'));
  return (
    <Dialog
      title={d ? d.pricelist.name : t('pl.new')}
      onClose={onClose}
      size="lg"
      footer={
        canEdit ? (
          <>
            <span className="foot-actions">
              {d ? (
                <Button variant="ghost" icon={d.pricelist.active ? 'archive' : 'undo'} onClick={() => archive(!d.pricelist.active)} disabled={busy}>
                  {d.pricelist.active ? t('pl.archive') : t('pl.restore')}
                </Button>
              ) : null}
            </span>
            <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={bad}>
              {t('pl.save')}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('pl.name')} required>
            <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} disabled={!canEdit} data-autofocus={!d || undefined} />
          </Field>
          <Field label={t('pl.discount')} hint={t('pl.discount_hint')} error={pctBad ? t('pl.discount_bad') : null}>
            <span className="input-unit">
              <input className="input" inputMode="decimal" placeholder="0" value={discount} onChange={(e) => setDiscount(e.target.value)} disabled={!canEdit} />
              <span>%</span>
            </span>
          </Field>
        </div>
        <section className="item-moves">
          <h3>{t('pl.own_prices_title')}</h3>
          <p className="muted">{t('pl.own_prices_hint')}</p>
          {rows.length ? (
            <div className="pl-rows" role="table" aria-label={t('pl.own_prices_title')}>
              {rows.map((r, i) => {
                const p = byId.get(r.product);
                return (
                  <div key={i} className="pl-row" role="row">
                    <select className={`input${twice(i) ? ' invalid' : ''}`} aria-label={t('inv.product')} value={r.product} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { product: e.target.value, price: x.price || (byId.get(e.target.value) ? priceText(byId.get(e.target.value)!.base) : '') } : x)))} disabled={!canEdit}>
                      <option value="">{t('pl.pick_product')}</option>
                      {products
                        .filter((x) => x.id === r.product || !used.has(x.id))
                        .map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}
                            {x.sku ? ` · ${x.sku}` : ''}
                          </option>
                        ))}
                    </select>
                    <span className="input-unit">
                      <input className={`input${parsed[i]!.value === null ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('pl.price')} value={r.price} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} disabled={!canEdit} />
                      <span>{t('pl.without_vat')}</span>
                    </span>
                    <small className="muted">{p ? t('pl.normal', { price: money(p.base) }) : ''}</small>
                    {canEdit ? <IconButton icon="trash" label={t('pl.remove')} onClick={() => setRows(rows.filter((_, j) => j !== i))} /> : <span />}
                  </div>
                );
              })}
            </div>
          ) : null}
          {canEdit ? (
            <div className="row-actions">
              <Button icon="plus" onClick={() => setRows([...rows, { product: '', price: '' }])}>
                {t('pl.add_price')}
              </Button>
            </div>
          ) : null}
        </section>
        <Field label={t('inv.note')}>
          <textarea className="input textarea" rows={2} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} disabled={!canEdit} />
        </Field>
        {d ? (
          <p className="muted">{d.customers.length ? t('pl.used_by', { names: d.customers.map((c) => c.name).join(', ') }) : t('pl.used_by_none')}</p>
        ) : null}
      </div>
    </Dialog>
  );
}

